import React from "react";
import { hasItems } from "~/features/app/utils";
import { useDeepDiveGallery, type DeepDiveGalleryControl, type Segment } from "~/features/blocks/inventory/deep-dive-gallery/context";
export type AutoPlayStatus = "playing" | "paused";
type Props = {
  controls: DeepDiveGalleryControl[];
  targetId: string;
};
export function useAutoPlay(props: Props) {
  const {
    controls,
    targetId
  } = props;
  const {
    autoplayTimeout,
    activeStepIndex,
    setActiveStepIndex,
    playVideoSegment,
    autoPlayStatus,
    isVideoPlaying
  } = useDeepDiveGallery();
  const [targetElement, setTargetElement] = React.useState<HTMLElement | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setInterval>>(undefined);
  const shouldUpdateProgress = React.useRef(true);
  React.useEffect(() => {
    const target = document.getElementById(targetId);
    setTargetElement(target);
  }, [targetId]);
  React.useEffect(() => {
    let startTimestamp: number;
    function updateProgress(timestamp: number) {
      if (!shouldUpdateProgress.current || isVideoPlaying) {
        return;
      }
      if (!startTimestamp) {
        startTimestamp = timestamp;
      }
      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(elapsed / autoplayTimeout, 1);
      if (targetElement) {
        targetElement.style.setProperty("--autoplay-progress", `${progress.toFixed(3)}`);
      }
      if (progress < 1) {
        requestAnimationFrame(updateProgress);
      }
    }
    shouldUpdateProgress.current = autoPlayStatus !== "paused" && hasItems(controls);
    if (shouldUpdateProgress.current) {
      requestAnimationFrame(updateProgress);
    }
    function advanceStep() {
      if (!hasItems(controls) || autoPlayStatus === "paused" || isVideoPlaying) {
        return;
      }
      const nextStep = activeStepIndex >= controls.length - 1 ? 0 : activeStepIndex + 1;
      const nextSegment = {
        start: controls[nextStep]?.videoStart,
        end: controls[nextStep]?.videoEnd
      };
      playVideoSegment(nextSegment as Segment);
      setActiveStepIndex(nextStep);
    }
    if (!timerRef.current && autoPlayStatus !== "paused") {
      timerRef.current = setInterval(advanceStep, autoplayTimeout);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
      if (targetElement) {
        targetElement.style.setProperty("--autoplay-progress", "0");
      }
    };
  }, [activeStepIndex, autoPlayStatus, autoplayTimeout, controls, isVideoPlaying, playVideoSegment, setActiveStepIndex, targetElement]);
  return {
    timer: timerRef.current
  };
}